import * as EventTracker from '@src/analytics/EventTracker';
import { getDubDurationLimitNote } from '@src/components/aiVoices/dubDurationLimit';
import DubGenerateModal from '@src/components/aiVoices/DubGenerateModal';
import { buildDubInfoEntries } from '@src/components/aiVoices/dubInfoSummary';
import { MusicSeparationControl } from '@src/components/aiVoices/MusicSeparationControl';
import RedubLandedPulse from '@src/components/aiVoices/RedubLandedPulse';
import RedubSelectionPopover from '@src/components/aiVoices/RedubSelectionPopover';
import {
  type ReviewHandoff,
  reviewHandoffFrom,
  type ReviewHandoffProp,
  type ReviewHandoffRequest,
} from '@src/components/aiVoices/reviewHandoff';
import {
  useDubbedLanguagesRead,
  usePlayableDubbedLanguages,
} from '@src/components/aiVoices/useDubbedLanguages';
import { useDubGenerateModalController } from '@src/components/aiVoices/useDubGenerateModalController';
import { useDubPlayback } from '@src/components/aiVoices/useDubPlayback';
import { useKeepOriginalMusic } from '@src/components/aiVoices/useKeepOriginalMusic';
import { useMusicSeparationController } from '@src/components/aiVoices/useMusicSeparationController';
import { useRedubSelectionController } from '@src/components/aiVoices/useRedubSelectionController';
import { useRemoveDub } from '@src/components/aiVoices/useRemoveDub';
import { useVoiceActivation } from '@src/components/aiVoices/useVoiceActivation';
import { ConfirmModal } from '@src/components/common/ConfirmModal';
import PreviewProcessingOverlay from '@src/components/common/PreviewProcessingOverlay';
import { ClipPreviewControlBar } from '@src/components/content-components/ClipPreviewControlBar';
import CreatorStudioSpeakersPanel from '@src/components/sidepanel/CreatorStudioSpeakersPanel';
import {
  copyReviewLinkToastAction,
  reviewLinkFromParams,
} from '@src/components/transcript/reviewLinkToastAction';
import { isStoryTranscriptEditingLocked } from '@src/components/transcript/storyTranscriptEditLock';
import { Transcript } from '@src/components/transcript/Transcript';
import { TranscriptOverflowExtrasProvider } from '@src/components/transcript/TranscriptOverflowExtras';
import { TranslationGenerateModal } from '@src/components/transcript/TranslationGenerateModal';
import { TranslationReviewRestoreVeil } from '@src/components/transcript/TranslationReviewRestoreVeil';
import {
  REVIEW_PANEL_Z,
  type ReviewMode,
  type ReviewRightTab,
  TranslationReviewWorkspace,
} from '@src/components/transcript/TranslationReviewWorkspace';
import {
  type DubMicInfo,
  LANGUAGE_MENU_HELP_TOOLTIP_ID,
  LANGUAGE_MENU_TOOLTIP_ID,
  TranslationToggle,
} from '@src/components/transcript/TranslationToggle';
import { useTranslationGenerateModalController } from '@src/components/transcript/useTranslationGenerateModalController';
import { resolveReviewSavedCaptionStyle } from '@src/editor/services/reviewCaptionTrack';
import { usePlayerSpaceShortcut } from '@src/hooks/shortcuts/usePlayerSpaceShortcut';
import { useLanguageRemovalFallback } from '@src/hooks/useLanguageRemovalFallback';
import { useLocalPreviewVideoUrl } from '@src/hooks/useLocalPreviewVideoUrl';
import { useMissingTranslationCheck } from '@src/hooks/useMissingTranslationCheck';
import { useTranscriptLoadRekick } from '@src/hooks/useTranscriptLoadRekick';
import { useTranslationStoreHydration } from '@src/hooks/useTranslationStoreHydration';
import { saveClipFromTranscriptTab } from '@src/services/AIClipProducer';
import { err, ok } from '@src/services/common/Result';
import { combineSavedSegmentsFromTab } from '@src/services/transcript/savedSegmentCombine';
import { SavedSegmentService } from '@src/services/transcript/SavedSegmentService';
import SavePullQuoteService from '@src/services/transcript/SavePullQuoteService';
import {
  StoreContext,
  useAnalytics,
  useDatoClient,
  useDubbingStore,
  useStore,
  useTranscription,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';
import CloseIcon from '@src/svgs/CloseIcon';
import ClipsCameraIcon from '@src/svgs/quick-actions/VideoIcon';
import { ContentTab, Studio } from '@src/types.ts/general';
import { TranscriptionStatus } from '@src/types.ts/video';
import { formatDateTime } from '@src/utility/dateFormat';
import { extractErrorInfo } from '@src/utility/error';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import {
  getLastStoryLanguage,
  recordLastStoryLanguage,
} from '@src/utility/lastStoryLanguage';
import { LogManager } from '@src/utility/Logger';
import { isCompressedRenditionPending } from '@src/utility/storyReadiness';
import { storyHasTranscript } from '@src/utility/transcriptLoadRetry';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import {
  computeDubPeaks,
  computeOriginalPeaks,
  getDurationFromElements,
} from '@src/utility/waveformUtils';
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { stripClipPreviewParams } from '../clipPreviewUrlState';
import { createClipRootProxy } from '../hooks/clipTranscriptProxies';
import { useOpenClipPreview } from '../useOpenClipPreview';
import { clipProcessorDepsFromAdapter } from './clipProcessorDepsFromAdapter';
import FindClipsPanel from './FindClipsPanel';
import HighlightActionPopover from './HighlightActionPopover';
import { StoryTopicsFilterSlot } from './StoryTopicsFilterSlot';
import { TopicRailTooltip } from './TopicRailTooltip';
import { TranscriptPlayerWithMagnifier } from './TranscriptPlayerWithMagnifier';
import { TranscriptSpeakersAdapter } from './TranscriptSpeakersAdapter';
import TranscriptSpeakersWaveform from './TranscriptSpeakersWaveform';
import {
  RIGHT_COLUMN_SPAN,
  RIGHT_COLUMN_WIDTH,
  TranscriptTabContainer as Container,
  WAVEFORM_COLLAPSED_HEIGHT,
  WAVEFORM_EXPANDED_HEIGHT,
} from './transcriptTabLayout';
import { TranscriptTabSkeleton } from './TranscriptTabSkeleton';
import {
  buildViewParams,
  isReviewWrittenToUrl,
  isTranscriptReviewRequested,
  resolveHydratedViewState,
  shouldCloseReviewFromUrl,
} from './transcriptViewUrlState';
import { useCoProducer } from './useCoProducer';
import { useTopicFiltering } from './useTopicFiltering';
import { useTopicRailHover } from './useTopicRailHover';

import type {
  ExternalPlayerControls,
  VideoTimeRange,
} from './TranscriptVideoPlayer';
import type { PlaybackState } from '@src/components/content-components/hooks/usePlaybackState';
import type { TranslationViewMode } from '@src/components/transcript/alignedBlocks';
import type { ReviewMusicSeparation } from '@src/components/transcript/ReviewPlayerAudioPickers';
import type { HighlightHoverInfo } from '@src/hooks/useHighlightHoverPopover';
import type { RedubLanded } from '@src/services/dubbing/redubLanded';
import type { TranscriptElement } from '@src/types.ts/transcript';

const logger = LogManager.getLogger('component:TranscriptSpeakersContent');

const TAB_SWITCH_OPEN_DELAY_MS = 300;

interface Props {
  /**
   * Whether this tab is the one on screen. ContentView keeps every tab MOUNTED
   * (a remount costs a 10s+ DatoCMS fetch), so "mounted" is not "visible" here
   * and document-level shortcuts must be gated on this, or they fire for a tab
   * the user is not looking at.
   */
  active?: boolean;
}

/**
 * TranscriptSpeakersContent - Layout component rendering the transcript view
 * alongside the speakers panel in the Content Studio.
 *
 * Uses a TranscriptSpeakersAdapter to provide an independent editing context
 * (time, playback, undo/redo) while sharing story/detection state with the
 * real VideoCreatorStore. A scoped ClipPreviewEditorStore + RootStore proxy
 * ensures Transcript reads from the adapter's editor, not global state.
 */
const TranscriptSpeakersContent: React.FC<Props> = observer(props => {
  const { active = true } = props;
  const transcription = useTranscription();
  const videoCreator = useVideoCreatorStore();
  // PLAT-11228: the session's locally-held upload, playable before renditions.
  const localPreviewUrl = useLocalPreviewVideoUrl(videoCreator.story?.id);
  const datoClientStore = useDatoClient();
  const realRootStore = useStore();
  const analyticsStore = useAnalytics();
  const userIdentity = useUserIdentity();
  const dubbingStore = useDubbingStore();
  const flags = useFlagsCombination(userIdentity.currentRole);
  // Translation reviewers get a reduced tab: transcript + foreign-language
  // dropdown + review-translation page only. Speakers panel, video player,
  // waveform, and clip-creation actions are hidden; structural edits are off.
  const reviewerMode = flags.enableTranslationReviewerMode;
  // Story-level transcript editing is locked unless `enableStoryTranscriptEditing`
  // is on — see `storyTranscriptEditLock.ts` for why a word-count change here
  // breaks existing translations and clip captions.
  const storyEditingLocked = isStoryTranscriptEditingLocked(flags);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { open: openClipPreview } = useOpenClipPreview();
  const [zoomLevel, setZoomLevel] = useState(0);
  const [isWaveformVisible, setIsWaveformVisible] = useState(true);
  // PLAT-10949 — while the Highlights filter is in Show Only mode, Transcript
  // reports the active highlight time windows here and the preview player
  // limits playback to them (skipping the gaps). Null = normal playback.
  const [highlightPlaybackRanges, setHighlightPlaybackRanges] = useState<
    VideoTimeRange[] | null
  >(null);
  const handleZoomChange = useCallback(
    (level: number) => setZoomLevel(level),
    [],
  );

  // Per-view language selection — null = Original. Owned locally so other
  // surfaces (Creator Studio, redline, etc.) can hold their own selection
  // independently per the decoupled per-view architecture.
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  // PLAT-10597 (DE6) — Original↔Dubbed audio mode shell state (top-of-dropdown
  // toggle). Local for now; the durable story default + live playback are the
  // rest of T9. The toggle only shows for a selected non-original language.
  const [audioMode, setAudioMode] = useState<'original' | 'dubbed' | 'both'>(
    'original',
  );
  const generateModal = useTranslationGenerateModalController();
  // PLAT-10261: Translation Review workspace open state (whole-script entry).
  const [reviewOpen, setReviewOpen] = useState(false);
  // PLAT-10948: the review's view-mode granularity, lifted here so it persists
  // as the VALUE of `?translationReview` (whose presence is the open state) for a
  // shareable/refreshable review link. Seeded from the URL during hydration;
  // mirrored out by the workspace.
  const [reviewViewMode, setReviewViewMode] =
    useState<TranslationViewMode>('segment');
  // PLAT-10926: the workspace's two remaining view attributes, lifted for the
  // same reason and written as `?reviewPanel` / `?reviewRef`, so a restored link
  // lands on the same tab and reference column rather than only the same
  // language and granularity. The workspace owns them; this surface only mirrors
  // what it reports back.
  const [reviewPanel, setReviewPanel] = useState<ReviewRightTab>('changes');
  const [reviewReferenceLanguage, setReviewReferenceLanguage] = useState<
    string | null
  >(null);
  // PLAT-11662 — which job the review opens on: set by the control that opened
  // it (globe / microphone, Review Translation / Review Dub) or by the link;
  // null lets the workspace apply its own default. Written as `?reviewMode`.
  const [reviewMode, setReviewMode] = useState<ReviewMode | null>(null);
  // The workspace reports all four attributes together. Identity-checked so an
  // unchanged report doesn't re-render the surface (and doesn't churn the URL).
  const handleReviewViewStateChange = useCallback(
    (next: {
      viewMode: TranslationViewMode;
      panel: ReviewRightTab;
      referenceLanguage: string | null;
      reviewMode: ReviewMode;
    }) => {
      setReviewViewMode(next.viewMode);
      setReviewPanel(next.panel);
      setReviewReferenceLanguage(next.referenceLanguage);
      setReviewMode(next.reviewMode);
    },
    [],
  );
  // PLAT-11877 — Produce Clips under the video: the Video Clips tab with its
  // producer already open, through the same `?produce=true` the Home quick
  // actions use (`SocialClipsView` reads it once and strips it).
  const previewPending =
    isCompressedRenditionPending(videoCreator.story) && !localPreviewUrl;
  const openClipProducer = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', ContentTab.video_clip);
    params.set('produce', 'true');
    navigate({ search: `?${params.toString()}` });
  }, [navigate, searchParams]);
  // Secondary, xs: it rides the tab row (design-system.md, Button Hierarchy).
  const produceClipsButton = flags.contentStudioEnableClipProducer ? (
    <ProduceClipsButton
      type="button"
      data-testid="transcript-produce-clips"
      disabled={previewPending}
      title={
        previewPending ? 'Available once the video preview is ready' : undefined
      }
      onClick={openClipProducer}
    >
      <ClipsCameraIcon width="14" height="14" />
      <span>Produce Clips</span>
    </ProduceClipsButton>
  ) : undefined;

  // PLAT-11898 — the user's own picks are what the story remembers; the
  // hydration below and the removal fallback set the state without recording,
  // so a link someone sent never becomes "my" language for the story.
  const storyIdForMemory = videoCreator.story?.id;
  const pickLanguage = useCallback(
    (language: string | null) => {
      setSelectedLanguage(language);
      recordLastStoryLanguage(storyIdForMemory, language);
    },
    [storyIdForMemory],
  );
  const openReview = useCallback(
    (language: string, mode: ReviewMode) => {
      pickLanguage(language);
      setReviewMode(mode);
      setReviewOpen(true);
    },
    [pickLanguage],
  );

  // PLAT-12047 / PLAT-12056 — a re-dub or a fix asked for on this tab's
  // own transcript is handed to the review, where every per-line tool
  // lives: the review opens on Dubbing (re-dub) or Translation (fix), finds
  // the words, and opens the popover or the editor on them. Held in memory
  // only; dropped once consumed or when the review closes, so nothing
  // replays on a reload.
  const [pendingHandoff, setPendingHandoff] = useState<ReviewHandoff | null>(
    null,
  );
  const reviewHandoffNonceRef = useRef(0);
  const handOffToReview = useCallback(
    (language: string, request: ReviewHandoffRequest) => {
      reviewHandoffNonceRef.current += 1;
      setPendingHandoff(
        reviewHandoffFrom(request, reviewHandoffNonceRef.current),
      );
      openReview(language, request.kind === 'fix' ? 'translation' : 'dubbing');
    },
    [openReview],
  );
  useEffect(() => {
    if (!reviewOpen) setPendingHandoff(null);
  }, [reviewOpen]);
  // PLAT-10596 (T8): kick-off "Generate dub" popover, scoped to the selected
  // translated language. Gated on the AI-Voice flag + workspace dubbing gate.
  const dubModal = useDubGenerateModalController();
  // PLAT-12022: the selection re-dub popover, anchored to the highlighted
  // passage — both selection entries on this tab (its own transcript and the
  // Translation Review it opens) land here.
  const redubSelection = useRedubSelectionController();
  // The language the open popover re-dubs, kept by this render so its landing
  // still knows it after the popover was closed mid-run.
  const redubLanguage = redubSelection.current?.language ?? null;
  const { dubbingEnabled } = useVoiceActivation();
  const dubEntryEnabled = !!flags.enableAiVoices && dubbingEnabled;
  // PLAT-10621: BCP-47 codes that already have a dub → green mic in the dropdown.
  // PLAT-11704: read through the shared hook (one source of truth with Creator
  // Studio and the clip preview), re-read after every event that can change it
  // (`dubbedRead.refresh()` on translation, dub and remove) instead of patched
  // optimistically — and the SELECTED language is confirmed playable before
  // it counts, so a listed-but-unservable dub never shows dub controls.
  const dubbedRead = useDubbedLanguagesRead(videoCreator.story?.id ?? null);
  const dubbedLanguages = usePlayableDubbedLanguages({
    storyId: videoCreator.story?.id ?? null,
    languages: dubbedRead.languages,
    selectedLanguage,
    enabled: dubEntryEnabled,
  });
  // PLAT-10740: URL hydration waits until the dub list has settled for the
  // (dubbing-enabled, story) config of the *current* render — a list left
  // over from the previous story, or from the "dubbing off" phase while flags
  // load, must not validate a `voice=dubbed` link. Comparing the hook's
  // stored `settledFor` against the values derived in this render is what
  // makes this race-free.
  const dubbedSettledForThisConfig =
    dubbedRead.settledFor !== null &&
    dubbedRead.settledFor.storyId === (videoCreator.story?.id ?? null) &&
    dubbedRead.settledFor.enabled === dubEntryEnabled;
  // PLAT-10719: read `dubbedLanguages` via a ref inside the language-change
  // effect so switching languages defaults the audio mode WITHOUT the effect
  // re-running when a dub is later added/removed for the current language (that
  // path is owned by the generate `onSuccess` / remove handlers — re-running
  // here would clobber the user's live choice).
  const dubbedLanguagesRef = useRef(dubbedLanguages);
  dubbedLanguagesRef.current = dubbedLanguages;
  // PLAT-10740: skip exactly the one auto-select run triggered by the initial
  // URL hydration setting the language — otherwise the default below would
  // clobber the URL-derived voice. Every subsequent user switch runs normally.
  const skipAutoSelectOnceRef = useRef(false);
  // PLAT-10597/PLAT-10719: on a language SWITCH, default the Voice control to
  // Dubbed when that language already has a dub, else Original. Original (and
  // any not-yet-dubbed language) has nothing to play in Dubbed mode — that would
  // mute the video with silence — so it falls back to Original.
  useEffect(() => {
    if (skipAutoSelectOnceRef.current) {
      skipAutoSelectOnceRef.current = false;
      return;
    }
    const hasDub =
      dubEntryEnabled &&
      !!selectedLanguage &&
      dubbedLanguagesRef.current.includes(selectedLanguage);
    setAudioMode(hasDub ? 'dubbed' : 'original');
  }, [selectedLanguage, dubEntryEnabled]);
  // The Voice toggle + Music and Sounds section only apply to a language that
  // actually has a dub (language-specific): a non-dubbed language has no dub to
  // play and no separated bed to sit under it, so hide both until it's dubbed.
  const selectedLanguageHasDub =
    dubEntryEnabled &&
    !!selectedLanguage &&
    dubbedLanguages.includes(selectedLanguage);
  // PLAT-11704 — and when the language STOPS being dubbed while selected (its
  // translation was removed and made again, its dub soft-deleted, or the list
  // corrected itself after the per-language read), the Voice cannot stay on
  // Dubbed: that mutes the picture over a dub that no longer plays. Back to
  // Original, where the video's own voice and music are heard as recorded.
  useEffect(() => {
    if (!selectedLanguageHasDub) setAudioMode('original');
  }, [selectedLanguageHasDub]);
  // Bumped on every successful dub generate/re-dub so live playback re-fetches
  // the manifest + recomposes — otherwise a re-dub of the current language keeps
  // playing the stale buffer until a page reload (PLAT-10616).
  const [dubReloadToken, setDubReloadToken] = useState(0);
  // PLAT-12024 — the lines a re-dub just re-recorded, so the transcript can
  // light them up and the toast's action can bring them back. Set from the
  // run's own coverage, not from the selection, so what pulses is what was
  // actually made.
  const [redubLanded, setRedubLanded] = useState<RedubLanded | null>(null);
  const landRedub = useCallback((segmentIds: readonly string[]) => {
    if (segmentIds.length === 0) return;
    setRedubLanded({ segmentIds, at: new Date().toISOString() });
  }, []);
  const revealRedub = useCallback(() => {
    setRedubLanded(prev =>
      prev ? { ...prev, revealToken: (prev.revealToken ?? 0) + 1 } : prev,
    );
  }, []);
  const supportedLanguages = useTranslationStoreHydration();

  // PLAT-10597 (DE6): the clickable "Original Voice / AI Voice - {language}"
  // label in the waveform header. Shown only for a selected translated language
  // when dubbing is enabled; clicking flips the same audio-mode state as the
  // language-dropdown toggle. The language named depends on the mode: in Dubbed
  // mode it's the selected translation (what the AI voice speaks); in Original
  // mode it's the actual source/recorded language, not the current translation.
  const captionForLanguage = useCallback(
    (code: string) =>
      supportedLanguages.find(l => l.value === code)?.caption ??
      formatLanguageCaption(code),
    [supportedLanguages],
  );

  // Original → source/recorded language; AI (Dubbed) → the selected translation;
  // Both → original + dub playing simultaneously.
  const voiceLabelText =
    dubEntryEnabled && selectedLanguage
      ? audioMode === 'both'
        ? 'Original + AI Voice'
        : audioMode === 'dubbed'
          ? `AI Voice - ${captionForLanguage(selectedLanguage)}`
          : `Original Voice - ${captionForLanguage(transcription.language)}`
      : undefined;
  // Accent styling whenever a dub is part of what's playing (Dubbed or Both).
  const voiceLabelActive = audioMode !== 'original';
  // Cycle Original → AI → Both → Original on click (same state the dropdown
  // segmented toggle drives).
  const cycleAudioMode = useCallback(
    () =>
      setAudioMode(m =>
        m === 'original' ? 'dubbed' : m === 'dubbed' ? 'both' : 'original',
      ),
    [],
  );

  // PLAT-9700: fall back to Original when the active translation is removed.
  useLanguageRemovalFallback(selectedLanguage, () => setSelectedLanguage(null));

  // PLAT-10740: persist the selected language + audio mode in the URL so a
  // refresh — and a shared link — restores the same view instead of resetting
  // to Original. `lang` = BCP-47 code (absent ⇒ Original); `voice` = the audio
  // mode, only meaningful for a language that has a dub. The Translation
  // Review workspace rides the same mechanism: `review=1` + `view` / `panel` /
  // `ref` reopen it with the same attributes.
  const didHydrateRef = useRef(false);
  const [hydrated, setHydrated] = useState(false);
  const [openingLanguage, setOpeningLanguage] = useState<string | null>(null);

  // Hydrate once, when the data needed to validate the params is ready. Reading
  // `searchParams` here is safe: the write-back effect below is gated on
  // `hydrated`, so nothing rewrites the URL before this runs.
  useEffect(() => {
    if (didHydrateRef.current) return;
    if (supportedLanguages.length === 0) return; // org not loaded yet
    // Wait for LaunchDarkly so `dubEntryEnabled` is its real value, not a
    // transient `false` while flags load — otherwise a `voice=dubbed` link
    // hydrates as Original and write-back then rewrites the URL to `original`.
    if (!flags.initialized) return;
    // When dubbing is on, wait until the dub list is settled for THIS config
    // (see `dubbedSettledForThisConfig`) so `voice=dubbed` is validated against
    // the real list.
    if (dubEntryEnabled && !dubbedSettledForThisConfig) return;
    didHydrateRef.current = true;

    const {
      language,
      audioMode: hydratedMode,
      reviewOpen: hydratedReviewOpen,
      reviewViewMode: hydratedViewMode,
      reviewPanel: hydratedPanel,
      reviewReferenceLanguage: hydratedReferenceLanguage,
      reviewMode: hydratedReviewMode,
    } = resolveHydratedViewState({
      // PLAT-11898 — no `lang` in the URL (the story list, a fresh open):
      // the language this user last worked in on this story. Validated
      // below exactly as a link's `lang` is; a removed translation falls to
      // the same place a stale link does.
      langParam:
        searchParams.get('lang') ??
        getLastStoryLanguage(videoCreator.story?.id),
      voiceParam: searchParams.get('voice'),
      reviewParam: searchParams.get('translationReview'),
      panelParam: searchParams.get('reviewPanel'),
      modeParam: searchParams.get('reviewMode'),
      refParam: searchParams.get('reviewRef'),
      supportedCodes: supportedLanguages.map(l => l.value),
      dubbedLanguages,
      dubEntryEnabled,
      reviewEnabled: !!flags.enableTranslationReview,
    });

    if (language) {
      // Setting the language triggers the auto-select effect; skip that one run
      // so it doesn't overwrite the URL-derived voice we set right after.
      skipAutoSelectOnceRef.current = true;
      setSelectedLanguage(language);
      setOpeningLanguage(language);
    }
    setAudioMode(hydratedMode);
    // PLAT-10948: reopen the review (only when a language resolved) + restore
    // its granularity from a shared/refreshed link. PLAT-10926 adds the right-
    // hand tab and the proxy reference language; all four seed the workspace's
    // own state on mount.
    setReviewViewMode(hydratedViewMode);
    setReviewPanel(hydratedPanel);
    setReviewReferenceLanguage(hydratedReferenceLanguage);
    setReviewMode(hydratedReviewMode);
    setReviewOpen(hydratedReviewOpen);
    setHydrated(true);
  }, [
    supportedLanguages,
    flags.initialized,
    flags.enableTranslationReview,
    dubEntryEnabled,
    dubbedSettledForThisConfig,
    dubbedLanguages,
    searchParams,
  ]);

  // PLAT-11898 — the opening language (a link's `lang` or the memory) was only
  // checked against the org's languages above. Confirm in the background that
  // the story still has it: one deleted elsewhere falls back to Original and is
  // forgotten, as a live removal does, unless the user has picked since.
  useMissingTranslationCheck(videoCreator.story?.id, openingLanguage, () => {
    if (selectedLanguage !== openingLanguage) return;
    setSelectedLanguage(null);
    if (getLastStoryLanguage(videoCreator.story?.id) === openingLanguage) {
      recordLastStoryLanguage(videoCreator.story?.id, null);
    }
  });

  // PLAT-11656 — whether the open review has been written into the URL yet.
  // The page-layout review leaves the app sidebar clickable, and its links
  // rebuild the query string without the review params; once we have written
  // them, their absence means the reviewer navigated away, so the review
  // closes instead of the write-back putting them straight back.
  const reviewSyncedRef = useRef(false);

  // Keep the URL in sync with the current selection. Gated on `hydrated` so it
  // never runs before hydration reads the URL.
  useEffect(() => {
    if (!hydrated) return;
    if (
      shouldCloseReviewFromUrl({
        reviewOpen,
        reviewSynced: reviewSyncedRef.current,
        params: searchParams,
      })
    ) {
      reviewSyncedRef.current = false;
      setReviewOpen(false);
      return;
    }
    const params = buildViewParams(searchParams, {
      selectedLanguage,
      audioMode,
      dubEntryEnabled,
      dubbedLanguages,
      reviewOpen,
      reviewViewMode,
      reviewPanel,
      reviewReferenceLanguage,
      reviewMode,
    });
    // PLAT-12055 — "written" means the router has delivered the review
    // params, not that we are about to send them: until then a re-run on
    // stale params must not read their absence as the reviewer leaving.
    reviewSyncedRef.current = isReviewWrittenToUrl({
      reviewOpen,
      selectedLanguage,
      params: searchParams,
    });
    const next = params.toString();
    if (next !== searchParams.toString()) {
      navigate({ search: next ? `?${next}` : '' }, { replace: true });
    }
  }, [
    hydrated,
    selectedLanguage,
    audioMode,
    dubEntryEnabled,
    dubbedLanguages,
    reviewOpen,
    reviewViewMode,
    reviewPanel,
    reviewReferenceLanguage,
    reviewMode,
    searchParams,
    navigate,
  ]);

  // The review reviews a translation, so falling back to Original closes it.
  // `buildViewParams` already drops the params in that case; this keeps the
  // state in step, so re-picking a language doesn't silently reopen a workspace
  // the user never asked for (and re-add its params to the URL).
  useEffect(() => {
    if (!hydrated || selectedLanguage) return;
    setReviewOpen(false);
  }, [hydrated, selectedLanguage]);

  // ── Topic filtering (feature-flagged) ──────────────────────────────
  const topicFilter = useTopicFiltering({
    enabled: !!flags.contentStudioTranscriptSpeakersEnableTopicFiltering,
    story: videoCreator.story ?? null,
    originalElements: transcription.originalTranscription?.elements,
    isTranscriptionReady: transcription.isTranscriptionReady,
    userIdentity: userIdentity.currentIdentity ?? undefined,
  });

  // Hovering a topic rail in the left gutter names its topic(s).
  const topicRailHover = useTopicRailHover(
    topicFilter.transcriptPaneRef,
    topicFilter.paragraphTopicTitles,
  );

  // ── Scoped stores & adapter (recreated per story) ─────────────────
  // Keyed on storyId so switching stories produces a fresh editor +
  // VideoSourceStore + adapter. Without this the adapter retains the
  // previous story's transcript until the component is unmounted.
  const currentStoryId = videoCreator.story?.id ?? null;

  // PLAT-10732: dub-info overlay rows per language, filled lazily on first
  // green-mic hover. The manifest read already carries provenance/handler/
  // alignment; we resolve voice names locally via the DubbingStore catalog.
  const [dubInfoByLanguage, setDubInfoByLanguage] = useState<
    Record<string, DubMicInfo>
  >({});
  const dubInfoInFlightRef = useRef(new Set<string>());
  const handleDubInfoHover = useCallback(
    (lang: string) => {
      if (!currentStoryId) return;
      // Load once — a cached (ready/loading/error) entry or an in-flight fetch
      // means there's nothing to do on hover.
      if (dubInfoByLanguage[lang] || dubInfoInFlightRef.current.has(lang)) {
        return;
      }
      dubInfoInFlightRef.current.add(lang);
      setDubInfoByLanguage(prev => ({
        ...prev,
        [lang]: { status: 'loading' },
      }));
      void datoClientStore.dubbingRepository
        .getDubManifest({ storyId: currentStoryId, language: lang })
        .then(resolved => {
          const content = resolved
            ? buildDubInfoEntries(resolved.manifest, {
                voiceName: id => dubbingStore.voiceById(id)?.name,
                voiceProvider: id => dubbingStore.voiceById(id)?.provider,
                formatDate: iso => formatDateTime(iso),
                languageCaption: captionForLanguage,
              })
            : null;
          setDubInfoByLanguage(prev => ({
            ...prev,
            [lang]: content
              ? {
                  status: 'ready',
                  entries: content.entries,
                  language: content.language,
                  fullDubSpeedUp: content.fullDubSpeedUp,
                  slidSegments: content.slidSegments,
                  spedUpSegments: content.spedUpSegments,
                  // PLAT-11799 — what this take was asked for, how each voice
                  // model resolved it, and what each line measured.
                  direction: content.direction,
                }
              : { status: 'error' },
          }));
        })
        .catch(() => {
          setDubInfoByLanguage(prev => ({
            ...prev,
            [lang]: { status: 'error' },
          }));
        })
        .finally(() => dubInfoInFlightRef.current.delete(lang));
    },
    [
      currentStoryId,
      datoClientStore,
      dubbingStore,
      captionForLanguage,
      dubInfoByLanguage,
    ],
  );

  // PLAT-12022 — what a dub run landing on this tab does, whichever surface
  // ran it (the Dub window or the Redub Selection popover). The server now
  // holds a new take, so: read the dubbed-language list again rather than
  // patch it (PLAT-11704), drop the cached dub-info card for that language so
  // the next hover reads the new take's voices and direction (PLAT-10732),
  // and make live playback re-read the dub (PLAT-10616). Only the Dub window
  // also switches the Voice to Dubbed (PLAT-10719); a selection re-dub leaves
  // the Voice where the person had it.
  const handleDubLanded = useCallback(
    (lang: string | null, options: { selectDubbed: boolean }) => {
      if (lang) {
        dubbedRead.refresh();
        if (options.selectDubbed && lang === selectedLanguage) {
          setAudioMode('dubbed');
        }
        setDubInfoByLanguage(prev => {
          if (!prev[lang]) return prev;
          const next = { ...prev };
          delete next[lang];
          return next;
        });
      }
      setDubReloadToken(t => t + 1);
    },
    [dubbedRead, selectedLanguage],
  );

  const videoSourceStore = useMemo(
    () => new VideoSourceStore(analyticsStore),
    [analyticsStore, currentStoryId],
  );

  const adapter = useMemo(
    () =>
      new TranscriptSpeakersAdapter({
        realVideoCreator: videoCreator,
        videoSourceStore,
        transcriptRepository: datoClientStore.transcriptRepository,
        transcriptionStore: transcription,
      }),
    [
      videoCreator,
      videoSourceStore,
      datoClientStore,
      transcription,
      currentStoryId,
    ],
  );

  // The adapter starts locked; report the flag so a LaunchDarkly change takes
  // effect on the next command without re-creating the editor.
  useEffect(() => {
    adapter.setStoryTranscriptEditingLocked(storyEditingLocked);
  }, [adapter, storyEditingLocked]);

  const scopedEditor = adapter.getScopedEditor();
  // PLAT-11683 / PLAT-11702 — the showcase's caption template at the story's
  // aspect ratio, the review's style for a story with no captions of its own:
  // the template this showcase used most recently, else its first. Keyed on
  // the showcase's templates and last-used list so a pick made mid-session
  // shows on the next open.
  const reviewCaptionAspectRatio = videoCreator.getCurrentAspectRatio();
  const showcaseKaraokeTemplates =
    videoCreator.organization?.karaokeTextBranding;
  const showcaseLastUsedTemplates =
    videoCreator.organization?.lastUsedTemplates;
  // PLAT-11702 follow-up — the whole list, so the review's Captions dropdown
  // can offer every one of the showcase's styles for a story with none of its
  // own. The head IS the preferred template, so the seed below cannot drift
  // from what the picker shows as selected.
  const reviewCaptionTemplates = useMemo(
    () =>
      videoCreator.textBrandingService.getCustomKaraokeTemplateConfigs(
        reviewCaptionAspectRatio,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      videoCreator,
      reviewCaptionAspectRatio,
      showcaseKaraokeTemplates,
      showcaseLastUsedTemplates,
    ],
  );
  const reviewCaptionFallbackTemplate = reviewCaptionTemplates[0];
  // PLAT-11702 follow-up — names an id the list above does not carry (a
  // preset, a language variant, another aspect ratio), so the review can
  // name the row standing for the video's own caption style.
  const resolveCaptionTemplateName = useCallback(
    (templateId: string) =>
      videoCreator.textBrandingService.getKaraokeTemplateTitle(templateId),
    [videoCreator],
  );
  const isPresetCaptionTemplate = useCallback(
    (templateId: string) =>
      videoCreator.textBrandingService.isPresetTemplate(templateId),
    [videoCreator],
  );
  // PLAT-11702 — the story's own caption style counts only when it IS a style
  // the showcase set up: a config still on the built-in default (no template
  // behind it) is Creator Studio's first-produce look, not the showcase's, so
  // the review shows the showcase template instead — when the showcase has one.
  const storyCaptionStyle = realRootStore.karaokeStore.hasKaraokeLines
    ? realRootStore.karaokeStore.defaultKaraokeConfig
    : null;
  const storySavedCaptionStyle = resolveReviewSavedCaptionStyle(
    storyCaptionStyle,
    !!reviewCaptionFallbackTemplate,
  );
  // PLAT-11683 — a STABLE identity for the review's karaoke refresh. The review
  // effect that draws story captions depends on this callback; calling it
  // reassigns the scoped editor's observable `karaokeLines`, which re-renders
  // this observer host — so a fresh arrow every render would re-fire the effect,
  // which refreshes again, an unbounded loop. `scopedEditor` is a stable field
  // on the memoized adapter.
  const refreshReviewKaraokeState = useCallback(
    () => scopedEditor.refreshKaraokeState(),
    [scopedEditor],
  );

  // PLAT-11618 — Space plays/pauses the tab's player. The verbs come from
  // whichever player is mounted (docked in reviewer mode, in the right column
  // otherwise); only one is ever up, so one slot holds them. The transcript's
  // own Space handler cannot do this job: it drives `adapter.refreshSourceAndPlay`,
  // which is a deliberate no-op here (this tab has no renderer), so without
  // this the key fell through to the browser and scrolled the transcript.
  const [playerControls, setPlayerControls] =
    useState<ExternalPlayerControls | null>(null);
  // PLAT-11877 — the player wears the review's control bar, which registers
  // the Space shortcut itself (standing down under a modal backdrop, so the
  // translation review workspace above owns the key while it is open). The
  // bar is mounted only while this tab is the active one: this tab stays
  // mounted for the whole Content Studio session, and the Clips tab's own
  // bar must not find a second listener here. The scrub state below is the
  // bar's own (the story player's clock is the adapter's), as in the
  // review's story entry.
  // The Co-Producer's Play All (dev) drives the same player; Space itself
  // belongs to the control bar.
  const togglePlayback = useCallback(() => {
    if (!playerControls?.isReady) return;
    if (adapter.isPlaying) playerControls.pause();
    else playerControls.play();
  }, [playerControls, adapter]);
  // The reviewer layout mounts no control bar, so the tab claims Space itself.
  usePlayerSpaceShortcut(togglePlayback, { enabled: active && reviewerMode });
  const [storySeekTime, setStorySeekTime] = useState<number | null>(null);
  const [storySeeking, setStorySeeking] = useState(false);
  const barPlaybackState: PlaybackState = {
    isPlaying: adapter.isPlaying,
    currentTime: adapter.time,
    duration: playerControls?.duration ?? 0,
    seekTime: storySeekTime,
    isSeeking: storySeeking,
  };

  // PLAT-10626: the music axis (None | Original) + the kept bed's signed URL.
  // Lifted here (not inside MusicSeparationControl) so the toggle and live
  // playback share one source of truth (DS6). Ephemeral, like the voice axis.
  // PLAT-10663: separation runs in the background — the popover can close freely
  // and only the outcome surfaces as a toast (mirroring runRemoveDub below), so
  // the user isn't tied to holding the dropdown open for the ~minutes-long split.
  const musicController = useMusicSeparationController(currentStoryId ?? '', {
    onDone: () => {
      adapter.showToast({
        state: 'success',
        message: 'Original music separated',
      });
    },
    onError: () => {
      adapter.showToast({
        state: 'error',
        message: "Couldn't separate voice and music. Try again.",
      });
    },
  });
  const { setMusicMode } = musicController;
  // DS1: reset the music axis to its default (From Original) on language change,
  // mirroring the voice axis reset above. A silent/absent bed is coerced back to
  // None by the controller's summary read.
  useEffect(() => {
    setMusicMode('original');
  }, [selectedLanguage, setMusicMode]);
  // DS5: the kept bed plays only under a Dubbed voice (Original/Both already
  // carry the source's own music via the unmuted video).
  const playMusicBed =
    audioMode === 'dubbed' && musicController.musicMode === 'original';

  // PLAT-10597 (DE4): in Dubbed mode play the composed dub over the muted video,
  // synced to the adapter clock. No-op unless a translated language is selected.
  // PLAT-10620: also reuse the composed buffer to draw the dubbed waveform.
  const { dubBuffer } = useDubPlayback({
    videoCreator: adapter,
    storyId: currentStoryId,
    language: selectedLanguage,
    // PLAT-11768 — stands down under the translation review, which is rendered
    // inside this tab and given this same adapter as its clock: both hooks
    // would answer one play and lay two copies of the dub over each other. It
    // went unheard while the copies matched, and became an echo on exactly the
    // line an automatic re-dub had just replaced — the review recomposes, this
    // one keeps the old take. Only playback stands down; `prepare` below keeps
    // the buffer (and the dubbed waveform) composed.
    active: !reviewOpen && (audioMode === 'dubbed' || audioMode === 'both'),
    // PLAT-10620: pre-warm the dub as soon as a dubbed language is selected so
    // switching to Dubbed renders the waveform + plays instantly (no fetch/decode
    // wait on toggle).
    prepare:
      dubEntryEnabled &&
      !!selectedLanguage &&
      dubbedLanguages.includes(selectedLanguage),
    reloadToken: dubReloadToken,
    // PLAT-10626: play the kept music bed under the dub when Music = Original.
    musicBedUrl: musicController.bedUrl,
    playMusicBed,
  });

  // PLAT-10620: derive the dubbed waveform peaks from the exact buffer being
  // played. Computed against the same story duration the waveform maps by, so
  // the dub bars align with the transcript clock (silence in gaps + tail).
  const originalElements = transcription.originalTranscription?.elements as
    | TranscriptElement[]
    | undefined;
  // PLAT-10737: the waveform timeline spans the real video, not just the
  // transcribed span. The last transcript element's `end_ts` stops at the final
  // spoken word (e.g. an end-card with no speech is untranscribed), which left
  // the total short of the video length and the playhead overshooting it. Prefer
  // the actual video duration; fall back to the transcript end only if it's
  // unavailable (still loading).
  // While renditions are pending the populated duration is a placeholder
  // (the pipeline hasn't probed the file yet) — the transcript end is the
  // truth until then.
  const videoDurationSec = isCompressedRenditionPending(videoCreator.story)
    ? 0
    : (videoCreator.story?.originalVideo?.video?.duration ?? 0);
  const storyDurationSec = useMemo(
    () =>
      videoDurationSec > 0
        ? videoDurationSec
        : getDurationFromElements(originalElements, 0),
    [originalElements, videoDurationSec],
  );
  // ── Co-Producer chat (PLAT-11806, feature-flagged) ────────────────
  // A second tab in this column, beside Speakers, holding both modes:
  // Find Clips and Ask a Question. The player and its
  // transport stay below BOTH tabs, which is what lets a suggestion play in
  // the player the user already has rather than a second one (DS4/DS9).
  const clipChatEnabled =
    !reviewerMode && !!flags.contentStudioTranscriptSpeakersEnableClipChat;
  // The Co-Producer is the column's first tab and the one the page opens on;
  // `null` until the user picks, so a flag that arrives after mount still
  // lands there.
  const [rightTab, setRightTab] = useState<'speakers' | 'find-clips' | null>(
    null,
  );
  // A flag turned off (or a reviewer) must never strand the column on a tab
  // that no longer renders.
  const activeRightTab = clipChatEnabled
    ? (rightTab ?? 'find-clips')
    : 'speakers';

  // PLAT-11714 — the narrow view of the separation the Dubbing Review needs:
  // enough to offer the action and show the run, and nothing that acts on the
  // story (re-separate, the stem downloads, the splitter picker stay in the
  // language menu's row). Over the length cap, or while the initial state read
  // is still in flight, Separate is withheld with the reason rather than
  // offered dead.
  const separationTooLong =
    storyDurationSec > flags.stemSeparationMaxMinutes * 60;
  const reviewMusicSeparation: ReviewMusicSeparation = useMemo(
    () => ({
      separating: musicController.status === 'running',
      startedAt: musicController.runStartedAt,
      durationSec: storyDurationSec,
      hasStems: musicController.hasStems,
      noMusic: musicController.hasStems && musicController.noMusic,
      blockedReason: separationTooLong
        ? `Music separation is available for tracks ${flags.stemSeparationMaxMinutes} minutes or less.`
        : musicController.status === 'loading'
          ? 'Checking this story’s audio…'
          : null,
      onSeparate: musicController.separate,
      onCancel: musicController.cancel,
    }),
    [
      musicController.status,
      musicController.runStartedAt,
      musicController.hasStems,
      musicController.noMusic,
      musicController.separate,
      musicController.cancel,
      storyDurationSec,
      separationTooLong,
      flags.stemSeparationMaxMinutes,
    ],
  );
  // The dub popover's "Keep Original Music" box: held here, above the popover,
  // so a separation it starts outlives the popover closing on Generate.
  const keepOriginalMusic = useKeepOriginalMusic({
    controller: musicController,
    durationSec: storyDurationSec,
    maxMinutes: flags.stemSeparationMaxMinutes,
  });

  const dubbedPeaks = useMemo(() => {
    if (!dubBuffer) {
      logger.debug('dubbedPeaks: no dub buffer yet', {
        selectedLanguage,
        audioMode,
        storyDurationSec,
      });
      return null;
    }
    const peaks = computeDubPeaks(dubBuffer, storyDurationSec);
    logger.debug('dubbedPeaks: computed', {
      storyDurationSec,
      points: peaks.length,
      maxPeak: peaks.reduce((m, v) => (v > m ? v : m), 0),
    });
    return peaks;
    // selectedLanguage/audioMode are only for the diagnostic log; the peaks
    // depend solely on the buffer + duration.
  }, [dubBuffer, storyDurationSec]);
  // PLAT-11532 — this tab dubs the whole story, so the story's length is what
  // the cap is measured against. Set ⇒ the Dub button and Re-dub entry are
  // disabled and the sentence points at a clip instead.
  const dubUnavailableNote = getDubDurationLimitNote(
    storyDurationSec,
    flags.dubbingMaxMinutes,
    'story',
  );
  // PLAT-12047 / PLAT-12056 — what this tab's prose is told about handing
  // off: after the dub list and the duration cap, which decide whether a
  // re-dub is on offer. Over the cap the review is given no re-dub, so the
  // prose offers none either (a row that opens the review and does nothing).
  const reviewHandoff = useMemo<ReviewHandoffProp | undefined>(
    () =>
      flags.enableTranslationReview && selectedLanguage
        ? {
            request: request => handOffToReview(selectedLanguage, request),
            redub:
              dubEntryEnabled &&
              !dubUnavailableNote &&
              dubbedLanguages.includes(selectedLanguage),
          }
        : undefined,
    [
      flags.enableTranslationReview,
      selectedLanguage,
      handOffToReview,
      dubEntryEnabled,
      dubUnavailableNote,
      dubbedLanguages,
    ],
  );

  // PLAT-10737: remap the precomputed original waveform blob onto the same
  // `storyDurationSec` transcript clock, so Original and Dubbed use identical
  // time mapping and the last spoken segment reaches the end of the timeline.
  const originalPeaks = useMemo(
    () =>
      videoCreator.originalWaveForm
        ? computeOriginalPeaks(videoCreator.originalWaveForm, storyDurationSec)
        : null,
    [videoCreator.originalWaveForm, storyDurationSec],
  );

  // PLAT-10617 (T14) / PLAT-11663: "Remove dub" — the confirm + delete +
  // toasts live in the shared hook; this surface only drops the green mic and,
  // if it was listening to that dub, falls back to Original.
  const removeDub = useRemoveDub({
    storyId: currentStoryId,
    repository: datoClientStore.dubbingRepository,
    captionForLanguage,
    showToast: adapter.showToast,
    onRemoved: useCallback(
      (lang: string) => {
        dubbedRead.refresh();
        if (selectedLanguage === lang) setAudioMode('original');
      },
      [selectedLanguage, dubbedRead],
    ),
  });

  const scopedRootProxy = useMemo(
    () => createClipRootProxy(realRootStore, scopedEditor, videoSourceStore),
    [realRootStore, scopedEditor, videoSourceStore],
  );

  // ── Initialize editor when transcription is ready ──────────────────
  const initializedForStoryIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!transcription.isTranscriptionReady || !currentStoryId) return;
    if (initializedForStoryIdRef.current === currentStoryId) return;

    initializedForStoryIdRef.current = currentStoryId;
    adapter.initializeEditor();
  }, [transcription.isTranscriptionReady, currentStoryId, adapter]);

  // PLAT-11697: retry a failed first transcript load once per story.
  useTranscriptLoadRekick({
    storyId: currentStoryId,
    hasTranscript: storyHasTranscript(videoCreator.story),
    isReady: transcription.isTranscriptionReady,
    isLoading: transcription.isTranscriptionLoading,
    load: () => videoCreator.loadTranscriptIfNeeded(),
  });

  // ── Cleanup on unmount ─────────────────────────────────────────────
  useEffect(() => {
    return () => {
      adapter.dispose();
    };
  }, [adapter]);

  // Create save services
  const saveService = useMemo(
    () => ({
      save: async (_asFinal: boolean) => {
        try {
          await adapter.flushPersistence();
          return ok({ republishRequired: false });
        } catch (error) {
          return err(
            'UNKNOWN',
            error instanceof Error
              ? error.message
              : 'Failed to save transcript',
            error instanceof Error ? error : String(error),
          );
        }
      },
    }),
    [adapter],
  );

  const savePullQuoteService = useMemo(
    () =>
      new SavePullQuoteService({
        datoClientStore,
      }),
    [datoClientStore],
  );

  const clipSaveService = useMemo(
    () => ({
      saveClip: async (input: {
        startIndex: number;
        endIndex: number;
        relevantElements?: any[];
      }) =>
        saveClipFromTranscriptTab(
          clipProcessorDepsFromAdapter(adapter),
          adapter.buildVideoSnapshot(),
          {
            theme: 'social clip',
            transcriptPosition: {
              startIndex: input.startIndex,
              endIndex: input.endIndex,
            },
          },
        ),
    }),
    [adapter],
  );

  // --- Saved segments (save-for-later → combine) ---
  const savedSegmentPersistence = useMemo(
    () => new SavedSegmentService({ datoClientStore }),
    [datoClientStore],
  );

  const savedSegmentService = useMemo(
    () => ({
      saveSegment: async (input: {
        name: string;
        startIndex: number;
        endIndex: number;
        color?: string;
      }) => {
        const result = await savedSegmentPersistence.addSegment(videoCreator, {
          ...input,
          username: userIdentity.currentEditor,
        });
        return result.ok ? result.value : null;
      },
      addRangeToSegment: async (input: {
        key: string;
        startIndex: number;
        endIndex: number;
      }) => {
        await savedSegmentPersistence.appendRange(
          videoCreator,
          Number(input.key),
          { startIndex: input.startIndex, endIndex: input.endIndex },
        );
      },
      renameSegment: async (key: string, name: string) => {
        await savedSegmentPersistence.renameSegment(
          videoCreator,
          Number(key),
          name,
        );
      },
      removeSegment: async (key: string) => {
        await savedSegmentPersistence.removeSegment(videoCreator, Number(key));
      },
      createClipFromSegment: (key: string) => {
        const group = videoCreator.story?.savedSegments?.[Number(key)];
        const range = group?.ranges[0];
        if (!range) return Promise.resolve(null);
        return saveClipFromTranscriptTab(
          clipProcessorDepsFromAdapter(adapter),
          adapter.buildVideoSnapshot(),
          {
            theme: 'social clip',
            transcriptPosition: {
              startIndex: range.startIndex,
              endIndex: range.endIndex,
            },
          },
        );
      },
      combineSegment: (key: string) => {
        const group = videoCreator.story?.savedSegments?.[Number(key)];
        if (!group) return Promise.resolve(null);
        return combineSavedSegmentsFromTab(
          clipProcessorDepsFromAdapter(adapter),
          adapter.buildVideoSnapshot(),
          group.ranges,
          group.name,
          scopedEditor.editor.getFullTranscript() as TranscriptElement[],
        );
      },
    }),
    [
      adapter,
      videoCreator,
      scopedEditor,
      savedSegmentPersistence,
      userIdentity,
    ],
  );

  const handleRemoveSegment = useCallback(
    async (key: string) => {
      await savedSegmentService.removeSegment(key);
    },
    [savedSegmentService],
  );

  const handleRenameSegment = useCallback(
    (key: string, name: string) => {
      void savedSegmentService.renameSegment(key, name);
    },
    [savedSegmentService],
  );

  const handleOpenClip = useCallback(
    (clipId: string) => {
      const clip = videoCreator.story?.otherVideos?.find(v => v.id === clipId);
      if (!clip) {
        logger.warn('Clip not found for popover open', { clipId });
        return;
      }
      // PLAT-10948 — strip a stale `?clipId` before navigating: otherwise the
      // clips view's URL sync opens THAT clip on mount and this one replaces it
      // a tick later (wrong-clip flash).
      const params = stripClipPreviewParams(searchParams);
      params.set('tab', ContentTab.video_clip);
      navigate({
        pathname: `/${Studio.content}`,
        search: params.toString(),
      });
      setTimeout(() => {
        openClipPreview(clip);
      }, TAB_SWITCH_OPEN_DELAY_MS);
    },
    [videoCreator.story?.otherVideos, searchParams, navigate, openClipPreview],
  );

  const coProducer = useCoProducer({
    enabled: clipChatEnabled,
    videoCreator,
    rootStore: realRootStore,
    adapter,
    transcription,
    flags,
    originalElements,
    storyDurationSec,
    playerControls,
    savedSegmentService,
    topicFilter,
    highlightPlaybackRanges,
    onOpenClip: handleOpenClip,
  });

  // --- Highlight popover handlers ---
  const handleOpenQuote = useCallback(
    (quoteId: string) => {
      const card = videoCreator.story?.shareableImages?.find(
        img => img.id === quoteId,
      );
      if (!card) {
        logger.warn('Quote not found for popover open', { quoteId });
        return;
      }
      // PLAT-10948 — drop any clip-preview params so landing on Pull Quotes
      // doesn't leave a `?clipId` that reopens the preview later.
      const params = stripClipPreviewParams(searchParams);
      params.set('tab', ContentTab.pull_quotes);
      navigate({
        pathname: `/${Studio.content}`,
        search: params.toString(),
      });
      // Allow PullQuotes view to mount before dispatching the open event.
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent('open-pull-quote', { detail: quoteId }),
        );
      }, TAB_SWITCH_OPEN_DELAY_MS);
    },
    [videoCreator.story?.shareableImages, searchParams, navigate],
  );

  const handleCreateClipFromSegment = useCallback(
    async (key: string) => {
      // Shown before the await so the click has immediate feedback; replaced
      // in place with the outcome (see `handleSaveAsClip`).
      const toastId = videoCreator.showToast({
        state: 'pending',
        message: 'Saving your clip…',
      });
      try {
        const saved = await savedSegmentService.createClipFromSegment(key);
        videoCreator.updateToast(
          toastId,
          saved
            ? {
                state: 'success',
                message: `Your clip "${saved.title}" has been saved`,
                action: {
                  label: 'Open Clip',
                  onClick: () => handleOpenClip(saved.id),
                },
              }
            : {
                state: 'error',
                message: 'Failed to create clip. Please try again.',
              },
        );
      } catch (error) {
        logger.error(
          'handleCreateClipFromSegment:failed',
          extractErrorInfo(error),
        );
        videoCreator.updateToast(toastId, {
          state: 'error',
          message: 'Failed to create clip. Please try again.',
        });
      }
    },
    [savedSegmentService, videoCreator, handleOpenClip],
  );

  // Keys with a combine in flight. Both entry points — the hover popover and
  // the Highlights dropdown row — dismiss themselves on click, so neither can
  // hold this: without it, reopening either and clicking again would combine
  // the same highlight twice.
  const combiningKeysRef = useRef(new Set<string>());

  /**
   * Both entry points are dismissible surfaces that close on click, so the
   * wait can't live in the control: it goes up as a `pending` toast before
   * the await and is replaced in place by the outcome.
   */
  const combineSegment = useCallback(
    async (key: string) => {
      if (combiningKeysRef.current.has(key)) return;
      combiningKeysRef.current.add(key);

      const group = videoCreator.story?.savedSegments?.[Number(key)];
      const name = group?.name ?? 'highlight';
      const toastId = videoCreator.showToast({
        state: 'pending',
        message: `Combining "${name}" into a reel…`,
      });

      try {
        const result = await savedSegmentService.combineSegment(key);
        if (result) {
          EventTracker.trackClipsCombined({
            story_id: videoCreator.story?.id,
            clips_count: group?.ranges.length ?? 0,
            source_platform: 'content-studio',
          });
          videoCreator.updateToast(toastId, {
            state: 'success',
            message: `Combined "${name}" into a reel`,
            action: {
              label: 'Open',
              onClick: () => handleOpenClip(result.id),
            },
          });
        } else {
          videoCreator.updateToast(toastId, {
            state: 'error',
            message: 'Could not combine highlight. Please try again.',
          });
        }
      } catch (error) {
        logger.error('combineSegment:failed', extractErrorInfo(error));
        videoCreator.updateToast(toastId, {
          state: 'error',
          message: 'Could not combine highlight. Please try again.',
        });
      } finally {
        combiningKeysRef.current.delete(key);
      }
    },
    [savedSegmentService, videoCreator, handleOpenClip],
  );

  const renderHighlightPopover = useCallback(
    (info: HighlightHoverInfo) => (
      <HighlightActionPopover
        anchorRect={info.anchorRect}
        quoteId={info.quoteId}
        clipId={info.clipId}
        savedSegmentId={info.savedSegmentId}
        segmentName={
          info.savedSegmentId
            ? videoCreator.story?.savedSegments?.[Number(info.savedSegmentId)]
                ?.name
            : undefined
        }
        segmentRangeCount={
          info.savedSegmentId
            ? (videoCreator.story?.savedSegments?.[Number(info.savedSegmentId)]
                ?.ranges.length ?? 0)
            : 0
        }
        onCreateClipFromSegment={handleCreateClipFromSegment}
        onCombineSegment={combineSegment}
        onRenameSegment={handleRenameSegment}
        onRemoveSegment={handleRemoveSegment}
        onOpenQuote={handleOpenQuote}
        onOpenClip={handleOpenClip}
        onMouseEnter={info.onMouseEnter}
        onMouseLeave={info.onMouseLeave}
      />
    ),
    [
      handleOpenQuote,
      handleOpenClip,
      videoCreator.story?.savedSegments,
      handleCreateClipFromSegment,
      combineSegment,
      handleRenameSegment,
      handleRemoveSegment,
    ],
  );

  const isTranscriptionReady = transcription?.isTranscriptionReady;
  const transcriptionFailed =
    transcription?.transcriptionLoadingStatus === TranscriptionStatus.Failed ||
    transcription?.transcriptionLoadingStatus ===
      TranscriptionStatus.GenerationFailed;

  // PLAT-12125 — the tab waits in its own shape: a skeleton of the transcript
  // pane, waveform card and player column, so nothing jumps when it lands. One
  // indicator, scoped to this tab (never a full-screen overlay: the tab is
  // reachable by a click on an already-loaded page, and the wait is only the
  // transcript).
  //
  // `isTranscriptionReady` is the moment there is something to read — the
  // story's transcript elements are in the store. The waveform, languages,
  // topics and player each fill their own region inside the real tab after
  // that, so the skeleton never waits on them.
  //
  // Failures fall through rather than holding the skeleton: `isTranscriptionReady`
  // is false for Failed/GenerationFailed too, and a loading state is never an
  // outcome — TranscriptContent has the failure copy and a re-generate action.
  if (!isTranscriptionReady && !transcriptionFailed) {
    return (
      <TranscriptTabSkeleton
        reviewerMode={reviewerMode}
        showRightTabs={clipChatEnabled}
      />
    );
  }

  return (
    <StoreContext.Provider value={scopedRootProxy}>
      <Container>
        <TranscriptPane
          $waveformCollapsed={!isWaveformVisible}
          $reviewerMode={reviewerMode}
          ref={topicFilter.transcriptPaneRef}
        >
          <TranscriptOverflowExtrasProvider>
            <Transcript
              surface="content_studio_transcript_tab"
              videoCreator={adapter}
              saveService={saveService}
              savePullQuoteService={savePullQuoteService}
              clipSaveService={clipSaveService}
              savedSegmentService={savedSegmentService}
              onCombineSegment={combineSegment}
              onHighlightPlaybackRangesChange={setHighlightPlaybackRanges}
              hideHeader={false}
              showUndoRedo={!reviewerMode}
              hideCaptions
              hideAddPhoto
              hideCutText
              hidePhotoHighlights
              hideRemoveText
              hideReplace={reviewerMode || storyEditingLocked}
              hideSaveAsClip={reviewerMode}
              showQuotesToggle={!reviewerMode}
              showClipsToggle={!reviewerMode}
              suspendAutoScroll={reviewOpen}
              translationReviewOpen={reviewOpen}
              renderHighlightPopover={
                reviewerMode ? undefined : renderHighlightPopover
              }
              language={selectedLanguage}
              enableInlineSpanReplace={flags.enableTranslationReview}
              // PLAT-12047 / PLAT-12056 — Fix Translation and Re-dub AI Voice
              // on this tab's prose hand off to the review (per-line work has
              // one home); a re-dub is on offer only when the language has a
              // dub (PLAT-10833), the same gate the review's own menu uses.
              reviewHandoff={reviewHandoff}
              enableChangedHighlight={flags.enableTranslationReview}
              visibleParagraphIds={
                coProducer.transcriptHighlightProps.visibleParagraphIds
              }
              highlightedParagraphIds={
                coProducer.transcriptHighlightProps.highlightedParagraphIds
              }
              paragraphHighlightColors={
                coProducer.transcriptHighlightProps.paragraphHighlightColors
              }
              activeParagraphId={
                coProducer.transcriptHighlightProps.activeParagraphId
              }
              headerExtras={{
                // PLAT-11182: the mirror of the function slot below — Highlights
                // goes inert the moment Story Topics' toggle reads Show Only,
                // not only once a topic is picked and the visibility set exists.
                filtersOwnShowOnly:
                  coProducer.transcriptHighlightProps.ownsShowOnly,
                // PLAT-11039: Story Topics renders in the filters slot so all
                // four content filters (Quotes, Clips, Highlights, Story
                // Topics) sit together; the translation UI stays in the tail.
                // Selected-topic chips render in the belowHeader row.
                // PLAT-11035: a translation reviewer gets no topic filter.
                // PLAT-11182: a function slot, so the dropdown reads Transcript's
                // own filter mode in the same render — the trigger inerts and the
                // topic selection clears for as long as Highlights owns Show
                // Only, not only while the highlights narrow something.
                filters: ({ highlightsShowOnlyActive }) =>
                  !reviewerMode &&
                  topicFilter.enabled &&
                  topicFilter.topics.length > 0 && (
                    <StoryTopicsFilterSlot
                      highlightsShowOnlyActive={highlightsShowOnlyActive}
                      topics={topicFilter.topics.map(t => t.title)}
                      selectedTopics={topicFilter.selectedTopics}
                      setSelectedTopics={topicFilter.setSelectedTopics}
                      mode={topicFilter.topicFilterMode}
                      onModeChange={topicFilter.setTopicFilterMode}
                      matchCounts={topicFilter.topicMatchCounts}
                      status={topicFilter.topicMappingStatus}
                      matchNav={{
                        current: topicFilter.activeMatchIndex + 1,
                        total: topicFilter.orderedMatchedParagraphIds.length,
                        onPrev: topicFilter.handlePrevMatch,
                        onNext: topicFilter.handleNextMatch,
                      }}
                    />
                  ),
                trailing: (
                  <TranslationToggle
                    selectedLanguage={selectedLanguage}
                    onLanguageChange={pickLanguage}
                    onRequestGenerate={generateModal.onRequestGenerate}
                    // PLAT-11563: row actions carry their own language, so a
                    // user can act on any translation without first switching
                    // the transcript to it. The review workspace is scoped to the
                    // displayed language, so opening it switches first.
                    onRequestReview={openReview}
                    onRequestDub={
                      dubEntryEnabled
                        ? language =>
                            // PLAT-10592: open in replace mode when a dub already
                            // exists for this language (the green-mic existence
                            // signal), else a first-time generate.
                            dubModal.open(language, {
                              replace: dubbedLanguages.includes(language),
                            })
                        : undefined
                    }
                    dubUnavailableNote={
                      dubEntryEnabled ? dubUnavailableNote : undefined
                    }
                    onRequestRemoveDub={
                      // PLAT-10617: stage the confirm; the toggle only surfaces
                      // this for a language that has a dub.
                      dubEntryEnabled ? removeDub.request : undefined
                    }
                    onRemoveDubForLanguage={
                      dubEntryEnabled ? removeDub.remove : undefined
                    }
                    audioMode={audioMode}
                    onAudioModeChange={
                      // Wire for any dubbing-enabled language, not only dubbed
                      // ones: the header hosts the Voice/Music controls when
                      // dubbed and the "Dub with AI Voice" CTA when not (PLAT-10664).
                      dubEntryEnabled ? setAudioMode : undefined
                    }
                    musicControl={
                      selectedLanguageHasDub && currentStoryId ? (
                        <MusicSeparationControl
                          controller={musicController}
                          tooltipId={LANGUAGE_MENU_TOOLTIP_ID}
                          helpTooltipId={LANGUAGE_MENU_HELP_TOOLTIP_ID}
                          storyId={currentStoryId}
                          storyName={videoCreator.story?.title}
                          showSplitterPicker={flags.enableStemSplitterPicker}
                          voiceIsDubbed={audioMode === 'dubbed'}
                          dubBuffer={dubBuffer}
                          dubLanguage={selectedLanguage}
                          durationSec={storyDurationSec}
                          maxMinutes={flags.stemSeparationMaxMinutes}
                        />
                      ) : undefined
                    }
                    dubbedLanguages={dubbedLanguages}
                    dubInfo={dubInfoByLanguage}
                    onDubInfoHover={handleDubInfoHover}
                    generateDisabled={generateModal.isOpen}
                    supportedLanguages={supportedLanguages}
                    surface="content-studio-transcript"
                    // PLAT-11877 — the callout under Add Translation, each
                    // time this tab is opened with the story untranslated.
                    firstTranslationHint={active}
                  />
                ),
              }}
              belowHeader={
                topicFilter.enabled &&
                topicFilter.topicMappingStatus === 'ready' &&
                topicFilter.selectedTopics.length > 0 ? (
                  <SelectedTopicTags>
                    {topicFilter.selectedTopics.map(topic => (
                      <SelectedTopicTag
                        key={topic}
                        $color={topicFilter.topicColorMap.get(topic)}
                      >
                        <SelectedTopicLabel>{topic}</SelectedTopicLabel>
                        <SelectedTopicRemoveButton
                          aria-label={`Remove ${topic} filter`}
                          $color={topicFilter.topicColorMap.get(topic)}
                          onClick={() =>
                            topicFilter.setSelectedTopics(prev =>
                              prev.filter(t => t !== topic),
                            )
                          }
                        >
                          <CloseIcon width={10} height={10} />
                        </SelectedTopicRemoveButton>
                      </SelectedTopicTag>
                    ))}
                    {/* PLAT-11039: the topic-match nav moved into the Story
                        Topics trigger (fused pill), superseding PLAT-11070's
                        left-anchored counter here; this row keeps chips only. */}
                  </SelectedTopicTags>
                ) : null
              }
              onOpenClip={handleOpenClip}
            />
          </TranscriptOverflowExtrasProvider>
          {/* PLAT-12024 — lights up the words a re-dub just landed on, inside
            this pane's own transcript (its walk up stops at this pane).
            Renders nothing itself, so it sits beside the transcript rather
            than in `belowHeader`: there it made the row under the header
            render, empty, in every state. Not while the review is open: the
            review lights the words in its own column, and a second pulse
            would play "Show Passage" twice. */}
          {currentStoryId && selectedLanguage && !reviewOpen && (
            <RedubLandedPulse
              storyId={currentStoryId}
              language={selectedLanguage}
              landed={redubLanded}
            />
          )}
        </TranscriptPane>
        {/* Outside the pane: it clips its overflow, and the tooltip sits in the
            gutter at its very edge. */}
        <TopicRailTooltip
          hover={topicRailHover}
          topicColorMap={topicFilter.topicColorMap}
        />
        {generateModal.isOpen && (
          <TranslationGenerateModal
            {...generateModal.modalProps}
            onSuccess={language => {
              pickLanguage(language);
              generateModal.close();
              // PLAT-11704 — a fresh translation is never dubbed; re-read the
              // list rather than trust whatever this surface last saw.
              dubbedRead.refresh();
            }}
            // PLAT-11574: checking the result is the usual next step, so the
            // "ready" toast opens the review workspace for that language —
            // only where the workspace exists (this surface, flag on).
            successAction={
              flags.enableTranslationReview
                ? language => ({
                    label: 'Review Translation',
                    onClick: () => openReview(language, 'translation'),
                  })
                : undefined
            }
            // PLAT-11877 — and a link to that same review, for a reviewer who
            // isn't in the room: the params `buildViewParams` would write once
            // the review is open on the new language, so the link restores
            // exactly what Review Translation opens.
            successSecondaryAction={
              flags.enableTranslationReview
                ? language =>
                    copyReviewLinkToastAction({
                      href: reviewLinkFromParams(
                        buildViewParams(searchParams, {
                          selectedLanguage: language,
                          audioMode,
                          dubEntryEnabled,
                          dubbedLanguages,
                          reviewOpen: true,
                          reviewViewMode,
                          reviewPanel,
                          reviewReferenceLanguage,
                          reviewMode: 'translation',
                        }),
                      ),
                      showToast: toast => videoCreator.showToast(toast),
                      analytics: {
                        story_id: videoCreator.story?.id,
                        language,
                        entry: 'story',
                        view_mode: reviewViewMode,
                      },
                    })
                : undefined
            }
            supportedLanguages={supportedLanguages}
            // Re-densify inserted-content indices + persist the editor's
            // transcript to S3 before the server translates it (PLAT-9511): no
            // `-1` content indices reach translation, and the stored
            // `originalIndices` match what this editor renders.
            onBeforeGenerate={() => adapter.reindexAndPersistForTranslation()}
          />
        )}
        {/* PLAT-11037 — a shared review link can't open the review on the first
            paint: hydration waits for the org's supported languages, LaunchDarkly
            and (when dubbing is on) the dub list, so this page would show through
            underneath first. Veil that window with the workspace's own backdrop;
            hydration always resolves, and it hands straight over to the workspace
            it seeds. Gated on the same `isTranscriptReviewRequested` hydration
            uses — its own copy of the check is how it silently stopped firing. */}
        {!hydrated && isTranscriptReviewRequested(searchParams) && (
          <TranslationReviewRestoreVeil zIndex={REVIEW_PANEL_Z} layout="page" />
        )}
        {reviewOpen && selectedLanguage && (
          <TranslationReviewWorkspace
            language={selectedLanguage}
            videoCreator={adapter}
            supportedLanguages={supportedLanguages}
            zoomLevel={zoomLevel}
            // PLAT-10600 (T12): enable the review's quick Original↔Dubbed toggle
            // + hear-both overlay when dubbing is on and this language has a dub;
            // seed its transient mode from the surface's current audio mode.
            enableDubMonitor={
              dubEntryEnabled && dubbedLanguages.includes(selectedLanguage)
            }
            // PLAT-11685 — the first dub, offered from inside Dubbing Review
            // when the reviewed language has none. The whole STORY's dub, as
            // the language menu's own Dub button is here; the modal is this
            // surface's, so the review never owns one.
            // Withheld over the duration cap, as the clip entry is: the review
            // has no room for the warning the language menu paints under its
            // own button, and `DubGenerateModal` checks no cap of its own.
            onRequestDub={
              dubEntryEnabled && !dubUnavailableNote
                ? language => dubModal.open(language, { replace: false })
                : undefined
            }
            // PLAT-11381 — this entry reviews the whole story's transcript.
            // Said outright rather than left for the review to infer.
            transcriptScope="story"
            // PLAT-11382 — re-dub the lines the reviewer selected. The same
            // popover, opened the same way, as the selection menu on this
            // tab's own transcript (`onRequestReDubSpan` above, PLAT-12022):
            // the selection's lines and speakers, which forces new audio for
            // lines that already have some. The direction field lives in that
            // popover.
            onRequestReDubSelection={
              dubEntryEnabled && !dubUnavailableNote
                ? (language, request) => redubSelection.open(language, request)
                : undefined
            }
            // PLAT-11768 — an automatic re-dub inside the review changed the
            // story's dub. This tab's own composed track is now the take from
            // before the edit, so re-read it: without this the reviewer closes
            // the review and is back on stale audio, with a waveform drawn from
            // it, until the page is reloaded.
            onDubUpdated={() => setDubReloadToken(token => token + 1)}
            // PLAT-12024 — the review lights the landed lines in its own
            // column, as this tab's transcript does.
            landedRedub={redubLanded}
            // PLAT-12047 — the passage this tab's menu handed over, if any.
            pendingHandoff={pendingHandoff}
            onHandoffConsumed={() => setPendingHandoff(null)}
            initialAudioMode={audioMode}
            // PLAT-11665 — the review's Music picker: seeded from this
            // surface's position, heard under the dub from the story's bed.
            initialMusicMode={musicController.musicMode}
            dubMusicBedUrl={musicController.bedUrl}
            dubVoiceStemUrl={
              musicController.voiceUrlMp3 ?? musicController.voiceUrlWav
            }
            // PLAT-11714 — and the way to MAKE that bed, for a reviewer who
            // finds the Music picker greyed out. The same controller the
            // language menu's row drives, so a run started in either place is
            // the one run, reported by the toasts wired above.
            musicSeparation={reviewMusicSeparation}
            // PLAT-10948 / PLAT-10926: restore + persist the review's view
            // attributes so a shared/refreshed link reopens on the same
            // granularity, right-hand tab and reference column. The write-back
            // effect above turns this state into the query params. The workspace
            // stays their owner and reports all three back through
            // `onViewStateChange` — a superset of `onViewModeChange`, so only
            // the one reporter is wired here (the clip entry still uses the
            // narrow one).
            initialViewMode={reviewViewMode}
            initialRightTab={reviewPanel}
            initialReferenceLanguage={reviewReferenceLanguage}
            initialReviewMode={reviewMode ?? undefined}
            onViewStateChange={handleReviewViewStateChange}
            // PLAT-11656 — a place beside the sidebar, not a dialog over the
            // page. Closing by state is the way back: the write-back above
            // drops the review params and keeps the language, so the URL is
            // the Transcript & Speakers tab the reviewer left.
            layout="page"
            // PLAT-11683 — the review draws captions over this entry's plain
            // player from the scoped editor (already loaded with the story's
            // transcript, translation resolver wired), styled as the story's
            // own captions when it has any, else from the showcase's caption
            // template, else the default. Review-only: the editor is left as
            // it was when the review closes.
            storyCaptions={{
              isReady: scopedEditor.isReady,
              editor: scopedEditor.editor,
              karaokeLines: scopedEditor.karaokeLines,
              refreshKaraokeState: refreshReviewKaraokeState,
              savedStyle: storySavedCaptionStyle,
            }}
            captionFallbackTemplate={reviewCaptionFallbackTemplate}
            captionTemplates={reviewCaptionTemplates}
            resolveCaptionTemplateName={resolveCaptionTemplateName}
            isPresetCaptionTemplate={isPresetCaptionTemplate}
            onClose={() => setReviewOpen(false)}
          />
        )}
        {dubModal.isOpen && dubModal.targetLanguage && currentStoryId && (
          <DubGenerateModal
            storyId={currentStoryId}
            targetLanguage={dubModal.targetLanguage}
            supportedLanguages={supportedLanguages}
            replace={dubModal.isReplace}
            segmentIds={dubModal.segmentIds ?? undefined}
            videoDurationSec={videoDurationSec || undefined}
            music={keepOriginalMusic}
            onClose={dubModal.close}
            // PLAT-11662: hearing the result is the usual next step, so the
            // "ready" toast opens Dubbing Review for that language — the dub
            // counterpart of the translation modal's "Review Translation".
            successAction={
              flags.enableTranslationReview
                ? language => ({
                    label: 'Review Dub',
                    onClick: () => openReview(language, 'dubbing'),
                  })
                : undefined
            }
            // Auto-select Dubbed for the just-dubbed language so the new dub
            // plays immediately instead of the original (PLAT-10719). The
            // PLAT-10597 language-change effect only defaults audioMode on a
            // language switch (it reads dubbedLanguages via a ref), so a dub
            // generated for the ALREADY-selected language would otherwise
            // leave the surface on Original.
            onSuccess={() =>
              handleDubLanded(dubModal.targetLanguage, { selectDubbed: true })
            }
          />
        )}
        {redubSelection.current && currentStoryId && (
          <RedubSelectionPopover
            storyId={currentStoryId}
            targetLanguage={redubSelection.current.language}
            supportedLanguages={supportedLanguages}
            request={redubSelection.current.request}
            onClose={redubSelection.close}
            onSuccess={result => {
              // The same landing as the Dub window's (PLAT-12022), without
              // switching the Voice: the dubbed list and the dub-info card
              // are read again, and so is what plays.
              handleDubLanded(redubLanguage, { selectDubbed: false });
              // PLAT-12024 — and show which words changed.
              landRedub(result.coverage.synthesized);
            }}
            onRevealPassage={revealRedub}
          />
        )}
        {removeDub.pendingLanguage && (
          <ConfirmModal
            closeModal={removeDub.cancel}
            onCancel={removeDub.cancel}
            onConfirm={removeDub.confirm}
            title={`Remove ${captionForLanguage(removeDub.pendingLanguage)} dub?`}
            confirmText="Remove dub"
            cancelText="Cancel"
          />
        )}
        {/* PLAT-11035 — of the three things reviewer mode hid, only the
            speakers panel stays hidden. A reviewer has to watch (and scrub)
            the moment to judge a translation, so the waveform spans the full
            width of the pane and the player docks over its bottom-right
            corner instead of riding in the (hidden) right column. Both keep
            their own collapse toggles for when the words matter more. */}
        <WaveformStrip
          $collapsed={!isWaveformVisible}
          $reviewerMode={reviewerMode}
        >
          <TranscriptSpeakersWaveform
            zoomLevel={zoomLevel}
            onZoomChange={handleZoomChange}
            isWaveformVisible={isWaveformVisible}
            onToggleWaveformVisible={() => setIsWaveformVisible(prev => !prev)}
            time={adapter.time}
            onSeek={t => adapter.setTime(t)}
            duration={storyDurationSec}
            originalPeaks={originalPeaks}
            originalTranscriptElements={originalElements}
            voiceLabel={voiceLabelText}
            voiceLabelActive={voiceLabelActive}
            // PLAT-11704: only a language with a playable dub can be cycled
            // into Dubbed. The label stays (it names what is playing) and
            // goes inert — a click here otherwise mutes the picture over a
            // dub that is not there.
            onToggleVoiceMode={
              selectedLanguageHasDub ? cycleAudioMode : undefined
            }
            dubbedPeaks={dubbedPeaks}
            showDubbed={audioMode === 'dubbed' && !!selectedLanguage}
            playbackSegments={coProducer.playbackRanges ?? undefined}
          />
        </WaveformStrip>
        {reviewerMode ? (
          <ReviewerPlayerDock $waveformCollapsed={!isWaveformVisible}>
            <TranscriptPlayerWithMagnifier
              videoCreator={adapter}
              zoomLevel={zoomLevel}
              playbackSegments={coProducer.playbackRanges ?? undefined}
              muted={audioMode === 'dubbed' && !!selectedLanguage}
              onControls={setPlayerControls}
            />
          </ReviewerPlayerDock>
        ) : (
          <RightColumn>
            {/* PLAT-11877 — the video leads the column; the speakers panel
                follows, with Produce Clips in its header beside Add Speaker:
                this tab is where a translation is checked before clips are
                made from it. */}
            <VideoPlayerArea>
              {/* PLAT-11224: audio-first — no preview rendition yet. The
                  player errors on its missing URL, so don't mount it —
                  unless this session still holds the uploaded file, which
                  the player falls back to (PLAT-11228). */}
              {previewPending ? (
                <ProcessingPlayerPlaceholder>
                  <PreviewProcessingOverlay compact />
                </ProcessingPlayerPlaceholder>
              ) : (
                <>
                  <TranscriptPlayerWithMagnifier
                    videoCreator={adapter}
                    zoomLevel={zoomLevel}
                    playbackSegments={coProducer.playbackRanges ?? undefined}
                    muted={audioMode === 'dubbed' && !!selectedLanguage}
                    onControls={setPlayerControls}
                    localPreviewUrl={localPreviewUrl}
                    // The review's player: controls in the bar below, no
                    // header row, no Hide Video (the tab keeps Hide Waveform),
                    // no zoom toggle.
                    externalControls
                    hideMagnifier
                  />
                  {active && (
                    <ClipPreviewControlBar
                      playbackState={barPlaybackState}
                      isReady={playerControls?.isReady ?? false}
                      play={() => playerControls?.play()}
                      pause={() => playerControls?.pause()}
                      setTime={time => playerControls?.setTime(time)}
                      setSeekTime={setStorySeekTime}
                      setIsSeeking={setStorySeeking}
                      // A 360px column, like the review's docked player.
                      compact
                      volumeControls={playerControls?.volume}
                    />
                  )}
                </>
              )}
            </VideoPlayerArea>
            {clipChatEnabled && (
              <RightTabStrip role="tablist" aria-label="Right panel">
                <RightTab
                  type="button"
                  role="tab"
                  aria-selected={activeRightTab === 'find-clips'}
                  $active={activeRightTab === 'find-clips'}
                  onClick={() => setRightTab('find-clips')}
                  data-testid="find-clips-tab"
                >
                  Co-Producer
                </RightTab>
                <RightTab
                  type="button"
                  role="tab"
                  aria-selected={activeRightTab === 'speakers'}
                  $active={activeRightTab === 'speakers'}
                  onClick={() => setRightTab('speakers')}
                  data-testid="speakers-tab"
                >
                  Speakers
                </RightTab>
                {/* Produce Clips rides the tab row, after the tabs,
                    reachable from either tab. */}
                {produceClipsButton && (
                  <RightTabActions>{produceClipsButton}</RightTabActions>
                )}
              </RightTabStrip>
            )}
            {activeRightTab === 'find-clips' ? (
              <FindClipsPane>
                <FindClipsPanel
                  {...coProducer.panelProps}
                  isPlaying={adapter.isPlaying}
                  onTogglePlayAll={togglePlayback}
                />
              </FindClipsPane>
            ) : (
              <SpeakersPane>
                <CreatorStudioSpeakersPanel
                  useOriginalTranscript
                  videoCreator={adapter}
                  // Without the Co-Producer's tab row, Produce Clips sits
                  // in its own row at the top of the panel instead.
                  headerActions={
                    clipChatEnabled ? undefined : produceClipsButton
                  }
                />
              </SpeakersPane>
            )}
          </RightColumn>
        )}
      </Container>
    </StoreContext.Provider>
  );
});

const TranscriptPane = styled.div<{
  $waveformCollapsed: boolean;
  $reviewerMode?: boolean;
}>`
  position: absolute;
  top: 0;
  left: 0;
  /* Reviewer mode hides the right speakers/player column (the waveform stays,
     full width), so the transcript spans the full width of the pane. */
  right: ${props => (props.$reviewerMode ? 0 : RIGHT_COLUMN_SPAN)}px;
  bottom: ${props =>
    props.$waveformCollapsed
      ? WAVEFORM_COLLAPSED_HEIGHT
      : WAVEFORM_EXPANDED_HEIGHT}px;
  overflow: hidden;
  transition: bottom 0.25s ease;

  /* PLAT-11039: neutralize Transcript Main's own 32px left indent (margin
     zeroed, and NO padding put back in its place — the pre-existing rule here
     converted it to padding-left: 32px, which kept the toolbar and text 32px
     right of the content tab strip no matter what the inner gutter did). The
     left inset now comes solely from the transcript's header/scroll gutter
     (see topicRailStyles), which is sized to line up with the tab strip;
     padding-right adds the breathing room the default (0 right) never had.
     Scoped to the direct child so the Creator Studio panel and the
     clip-preview instance keep their own insets. */
  & > #transcript-container {
    margin-left: 0;
    width: 100%;
    padding-left: 0;
    padding-right: var(--spacing-md);
  }
`;

const WaveformStrip = styled.div<{
  $collapsed: boolean;
  $reviewerMode?: boolean;
}>`
  position: absolute;
  bottom: 0;
  left: 0;
  /* No right column in reviewer mode, so the waveform runs the full width. */
  right: ${props => (props.$reviewerMode ? 0 : RIGHT_COLUMN_SPAN)}px;
  height: ${props =>
    props.$collapsed ? WAVEFORM_COLLAPSED_HEIGHT : WAVEFORM_EXPANDED_HEIGHT}px;
  transition: height 0.25s ease;
`;

/** Gap between the reviewer player dock and what it sits above. */
const DOCK_EDGE_GAP = 16;
/** Floor for the dock's bottom inset: it clears the Pylon support launcher
 *  (`right: 24px; bottom: 16px`, ~56px tall) the same way the bottom
 *  publish-drawer docks do (`PublishProgressDrawer`), so support stays
 *  clickable. Binds while the waveform is collapsed; expanded, the waveform
 *  is the taller of the two. */
const DOCK_PYLON_CLEARANCE = 88;

/** Reviewer-mode player dock: the right column's player, floated over the
 *  bottom-right of the full-width transcript, resting on the waveform. */
const ReviewerPlayerDock = styled.div<{ $waveformCollapsed: boolean }>`
  position: absolute;
  right: var(--spacing-md);
  bottom: ${props =>
    Math.max(
      (props.$waveformCollapsed
        ? WAVEFORM_COLLAPSED_HEIGHT
        : WAVEFORM_EXPANDED_HEIGHT) + DOCK_EDGE_GAP,
      DOCK_PYLON_CLEARANCE,
    )}px;
  transition: bottom 0.25s ease;
  width: 360px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  background: var(--background);
  box-shadow: var(--elevation-toast);
`;

const RightColumn = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${RIGHT_COLUMN_WIDTH}px;
  display: flex;
  flex-direction: column;
  background: var(--surface);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
`;

/**
 * PLAT-11806 — Co-Producer | Speakers, Co-Producer first and the default.
 * Underline tabs matching the page-level tab strip; the player and transport
 * below stay outside this switch, so changing tabs never interrupts playback.
 */
const RightTabStrip = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: flex-end;
  gap: 2px;
  padding: 0 8px;
  border-bottom: 1px solid var(--border);
`;

const RightTab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  border-bottom: 2px solid
    ${props => (props.$active ? 'var(--accent)' : 'transparent')};
  padding: 10px 12px;
  font-family: inherit;
  font-size: var(--text-sm);
  font-weight: ${props =>
    props.$active
      ? 'var(--font-weight-semi-bold)'
      : 'var(--font-weight-normal)'};
  color: ${props =>
    props.$active ? 'var(--foreground)' : 'var(--text-secondary)'};
  cursor: pointer;

  &:hover {
    color: var(--foreground);
  }
`;

/* After the tabs, centred on the strip's text line, clear of the rule. */
const RightTabActions = styled.div`
  margin-left: auto;
  align-self: center;
  padding: 0 4px 2px 8px;
`;

const FindClipsPane = styled.div`
  flex: 1;
  min-height: 0;
`;

const SpeakersPane = styled.div`
  flex: 1;
  min-height: 0; /* Allow flex item to shrink below content height so overflow-y works */
  overflow-y: auto;
  overflow-x: hidden;
  /* The panel's own header margin (--spacing-sm) makes up the rest of the
     12px between the player's bar and the action row. */
  padding: var(--spacing-xs) 12px 12px;
  box-sizing: border-box;
`;

const VideoPlayerArea = styled.div`
  position: relative;
  flex-shrink: 0;
  /* The bar's own surface, not black: the bar rounds its bottom corners, and
     black behind them showed as two dark notches under the picture. The
     picture itself is black inside the player. */
  background: var(--surface-elevated);
  display: flex;
  flex-direction: column;
`;

/* Secondary, xs — rides the Co-Producer | Speakers tab row (design-system.md,
   Button Hierarchy → Sizes): a solid green sm block beside 14px tab labels
   read as a banner, so it is the outline tier at the text-row size. */
const ProduceClipsButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: var(--btn-h-xs);
  padding: 0 var(--btn-px-xs);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: transparent;
  color: var(--foreground);
  font-family: inherit;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const ProcessingPlayerPlaceholder = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  background: #000;
`;

const SelectedTopicTags = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  flex-wrap: wrap;
`;

const SelectedTopicTag = styled.div<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: 999px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-text-xs);
  white-space: nowrap;
  background-color: var(--gray-600);
  color: ${props => props.$color || 'var(--badge-blue-text)'};
  max-width: 200px;
`;

const SelectedTopicLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SelectedTopicRemoveButton = styled.button<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: ${props => props.$color || 'var(--badge-blue-text)'};
  cursor: pointer;
  padding: 0;
  opacity: 0.7;

  &:hover {
    opacity: 1;
    background-color: color-mix(
      in srgb,
      ${props => props.$color || 'var(--badge-blue-text)'} 20%,
      transparent
    );
  }
`;

export default TranscriptSpeakersContent;
